/**
 * SpatialGrid library search for AI automation.
 * Prefer in-house library assets before falling back to Sketchfab.
 */

import {
  itemMatchesSubject,
  subjectRelevanceScore,
  normalizeModelSearchQuery,
} from './modelRelevance';

const MIN_ACCEPT_SCORE = 50;

/** Vague “add a glb / model / 3d” style queries — no specific subject. */
export const GENERIC_MODEL_QUERY_RE =
  /^(a\s+|an\s+|the\s+)?(glb|gltf|model|mesh|object|asset|3d(\s*models?)?|models?)s?$/i;

/** Rotating subjects when the user asks for a generic GLB. */
export const MODEL_VARIETY_QUERIES = [
  'chair',
  'table',
  'lamp',
  'plant',
  'car',
  'robot',
  'tree',
  'vase',
  'sofa',
  'bike',
  'cat',
  'house',
  'shoe',
  'guitar',
  'camera',
  'book',
  'cup',
  'desk',
  'flower',
  'boat',
];

export function isGenericModelQuery(query) {
  const q =
    normalizeModelSearchQuery(query) ||
    String(query || '')
      .toLowerCase()
      .trim();
  if (!q) return true;
  return GENERIC_MODEL_QUERY_RE.test(q);
}

/**
 * Rotate variety terms starting at offset so each "add a glb" tries a new subject.
 */
export function getVarietyQueries(
  offset = 0,
  count = MODEL_VARIETY_QUERIES.length
) {
  const n = MODEL_VARIETY_QUERIES.length;
  if (!n) return [];
  const start = (((Number(offset) || 0) % n) + n) % n;
  const out = [];
  const limit = Math.min(Math.max(count, 1), n);
  for (let i = 0; i < limit; i++) {
    out.push(MODEL_VARIETY_QUERIES[(start + i) % n]);
  }
  return out;
}

/**
 * @param {string} query
 * @param {{ limit?: number, type?: string }} [options]
 * @returns {Promise<object[]>}
 */
export async function searchLibraryModels(query, options = {}) {
  const q = String(query || '').trim();
  if (!q) return [];

  const { limit = 24, type = '3D' } = options;
  const base = process.env.REACT_APP_BASE_URL;
  if (!base) {
    console.warn('[librarySearch] REACT_APP_BASE_URL is not set');
    return [];
  }

  const token = localStorage.getItem('token') || '';
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const params = new URLSearchParams({
    search: q,
    isPublic: 'true',
    page: '1',
    limit: String(Math.min(limit, 50)),
  });
  if (type) params.set('type', type);

  try {
    const response = await fetch(`${base}library?${params.toString()}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      console.warn('[librarySearch] HTTP', response.status);
      return [];
    }
    const data = await response.json();
    const items = extractLibraryItems(data);

    if ((!items || items.length === 0) && type) {
      return searchLibraryModels(q, { ...options, type: '' });
    }

    return Array.isArray(items) ? items : [];
  } catch (error) {
    console.warn('[librarySearch] failed:', error?.message || error);
    return [];
  }
}

function extractLibraryItems(data) {
  if (!data) return [];
  let raw = [];
  if (Array.isArray(data.data)) raw = data.data;
  else if (Array.isArray(data.items)) raw = data.items;
  else if (Array.isArray(data.results)) raw = data.results;
  else if (data.data?.items && Array.isArray(data.data.items))
    raw = data.data.items;
  else if (data.data?.docs && Array.isArray(data.data.docs))
    raw = data.data.docs;
  else return [];

  return raw.flatMap((entry) => {
    if (entry && Array.isArray(entry.data)) return entry.data;
    return [entry];
  });
}

/**
 * Score a library item against the user query. Higher = better.
 * Unrelated names (no subject match) score 0.
 */
export function scoreLibraryItem(item, query) {
  if (!item) return 0;
  const q =
    normalizeModelSearchQuery(query) ||
    String(query || '')
      .toLowerCase()
      .trim();
  if (!q) return 0;

  if (!itemMatchesSubject(item, q)) return 0;

  const relevance = subjectRelevanceScore(item, q);
  if (relevance <= 0) return 0;

  let score = relevance;

  const type = String(item.type || '').toLowerCase();
  if (type === '3d' || type === 'mesh') score += 10;

  const hasUrl = !!(item.streamUrl || item.url || item.asset_file_path);
  if (hasUrl) score += 5;
  else score -= 100;

  return score;
}

/**
 * Pick the best library 3D asset for a query, or null if nothing is good enough.
 * Only accepts items whose name/tags match the subject.
 */
export async function findBestLibraryModel(query, options = {}) {
  const allowIds = new Set(
    (options.allowLibraryIds || []).map((id) => String(id))
  );
  const exclude = new Set(
    (options.excludeLibraryIds || [])
      .map((id) => String(id))
      .filter((id) => !allowIds.has(id))
  );
  const minScore = options.minScore ?? MIN_ACCEPT_SCORE;
  const subject =
    normalizeModelSearchQuery(query) || String(query || '').trim();
  const generic = isGenericModelQuery(subject);
  const requireUnique = options.requireUnique !== false;

  const searchTerms = generic
    ? getVarietyQueries(options.varietyOffset || 0, 8)
    : [subject, `${subject} 3d`].filter(Boolean);

  const seenIds = new Set();
  const scored = [];

  for (const term of searchTerms) {
    const items = await searchLibraryModels(term, options);
    for (const item of items) {
      const id = item?._id != null ? String(item._id) : '';
      if (!id || seenIds.has(id)) continue;
      if (requireUnique && exclude.has(id) && !allowIds.has(id)) continue;
      seenIds.add(id);

      const hasUrl = !!(item.streamUrl || item.url || item.asset_file_path);
      if (!hasUrl) continue;

      const scoreAgainst = generic ? term : subject;
      if (!itemMatchesSubject(item, scoreAgainst) && !allowIds.has(id)) {
        continue;
      }

      const score = scoreLibraryItem(item, scoreAgainst);
      if (score < minScore && !allowIds.has(id)) continue;

      const boost = allowIds.has(id) ? 1000 : 0;
      scored.push({ item, score: score + boost, term });
    }

    if (scored.length) break;
  }

  scored.sort((a, b) => b.score - a.score);
  if (!scored.length) return null;

  const unused = scored.filter(
    (s) =>
      !exclude.has(String(s.item._id)) || allowIds.has(String(s.item._id))
  );
  const pool = unused.length ? unused : requireUnique ? [] : scored;
  if (!pool.length) return null;

  // Specific queries: best match. Generic: rotate among on-subject matches.
  let pickIndex = 0;
  if (generic && pool.length > 1) {
    pickIndex =
      (Number(options.varietyOffset) || 0) % Math.min(pool.length, 8);
  }

  const pick = pool[pickIndex] || pool[0];
  return {
    item: pick.item,
    score: pick.score,
    searchTerm: pick.term,
    candidates: pool.slice(0, 8),
  };
}

export function sanitizeDisplayName(name, fallback = 'Model') {
  const raw = String(name || '').trim();
  if (!raw) return fallback;
  const cleaned = raw.replace(/[\\/<>:"|?*]/g, '_').slice(0, 120).trim();
  return cleaned || fallback;
}
